import * as roleService from '@/services/role.service';

import { queryKeys } from '@/query/query-keys';
import {
  notifyMutationErrorWithOptions,
  notifyMutationSuccessWithOptions,
} from '@/query/query-notifications';

import type { UpdateRoleFormValues } from '@/schemas/role.schema';

import type { DropdownOption, QueryOptions } from '@/types/common.type';
import type { Permission, Role } from '@/types/role.type';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

export const useFetchAllRolesQuery = (
  options?: Omit<UseQueryOptions<Role[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.roles.all,
    queryFn: () => roleService.fetchAllRoles(),
    ...options,
  });
};

export const useFetchRoleByIdQuery = (
  id: number,
  options?: Omit<UseQueryOptions<Role, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.roles.detail(id),
    queryFn: () => roleService.fetchRoleById(id),
    ...options,
  });
};

export const useCreateRoleMutation = (options?: QueryOptions<Role>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.roles.mutations.create,
    mutationFn: roleService.createRole,
    onSuccess: role => {
      notifyMutationSuccessWithOptions(
        options,
        'Role created',
        'A new role was created successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      options?.onSuccess?.(role);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Role creation failed', error);
      options?.onError?.(error);
    },
  });
};

export const useFetchAllPermissionsQuery = (
  options?: Omit<UseQueryOptions<Permission[], Error>, 'queryKey' | 'queryFn' | 'select'>
) => {
  return useQuery({
    queryKey: queryKeys.roles.permissions,
    queryFn: () => roleService.fetchAllPermissions(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: data => {
      return data.map(permission => ({
        label: permission.label,
        value: String(permission.id),
      })) as DropdownOption[];
    },
    ...options,
  });
};

export const useFetchRawPermissionsQuery = (
  options?: Omit<UseQueryOptions<Permission[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.roles.permissions,
    queryFn: () => roleService.fetchAllPermissions(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useUpdateRoleMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.roles.mutations.update,
    mutationFn: ({ id, payload }: { id: number; payload: UpdateRoleFormValues }) =>
      roleService.updateRole(id, payload),
    onSuccess: (_, { id }) => {
      notifyMutationSuccessWithOptions(
        options,
        'Role updated',
        'Role details were updated successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.detail(id) });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Role update failed', error);
      options?.onError?.(error);
    },
  });
};

export const useDeleteRoleMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.roles.mutations.delete,
    mutationFn: (id: number) => roleService.deleteRoleById(id),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(options, 'Role deleted', 'Role was deleted successfully.');
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Role deletion failed', error);
      options?.onError?.(error);
    },
  });
};
